import type { HLocation } from '@reach/router';
import qs from 'query-string';

import OneTrust, { ConsentGroup } from './OneTrust';
import { ScriptLoader } from './Script';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Google {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace OAuth2 {
      interface Client {
        requestAccessToken(): void;
      }

      interface Config {
        client_id: string;
        scope: string;
        callback: (response: { access_token: string }) => void;
      }

      interface API {
        initTokenClient(config: Config): Client;

        revoke(token: string, callback?: () => void): void;
      }
    }

    interface Client {
      accounts: {
        oauth2: OAuth2.API;
      };
    }
  }

  interface Window {
    google: Google.Client;
  }
}

export class GoogleLoginService {
  public static consentGroup = ConsentGroup.NECESSARY;

  private scriptLoader = new ScriptLoader(
    `https://accounts.google.com/gsi/client`,
    ConsentGroup.NECESSARY,
    'google-login',
  );
  private loaded = false;
  private client?: Google.OAuth2.Client;

  public get canLoad() {
    return OneTrust.isCookieConsentGroupAllowed(GoogleLoginService.consentGroup);
  }

  public get isBlocked() {
    return !this.canLoad || this.scriptLoader.isBlocked;
  }

  public load(callback: () => void, onSuccess: (token: string) => void, scope: string | string[]): void | (() => void) {
    return this.scriptLoader.load(() => {
      this.loaded = true;
      this.client = window.google?.accounts.oauth2.initTokenClient({
        client_id: process.env.GATSBY_GOOGLE_CLIENT_ID,
        scope: Array.isArray(scope) ? scope.join(' ') : scope,
        callback: response => onSuccess(response.access_token),
      });
      callback();
    });
  }

  public login(): void {
    if (!this.client) return;
    this.client.requestAccessToken();
  }
}

export function createGoogleAuthURL(location: HLocation, scope: string | string[]): string {
  const props = qs.stringify({
    client_id: process.env.GATSBY_GOOGLE_CLIENT_ID,
    response_type: 'token',
    scope: Array.isArray(scope) ? scope.join(' ') : scope,
    redirect_uri: `${window.location.origin}/sign/google`,
    state: qs.stringify({
      original_uri: `${location.pathname}${location.search}${location.hash}`,
    }),
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${props}`;
}
